import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { useLanguage } from '../contexts/LanguageContext';
import { Bell, Check, Package, CreditCard, Info } from '@phosphor-icons/react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { getNotifications, markNotificationRead, markAllNotificationsRead } from '../lib/api';
import { Button } from '../components/ui/button';

export default function NotificationsPage() {
  const { t, language , tx } = useLanguage();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await getNotifications();
      setNotifications(response.data);
    } catch (error) {
      toast.error(tx('Failed to fetch notifications', 'Error al cargar notificaciones', 'Failed to fetch notifications'));
    } finally {
      setLoading(false);
    }
  };

  const handleMarkRead = async (notificationId) => {
    try {
      await markNotificationRead(notificationId);
      setNotifications(notifications.map(n => 
        n.id === notificationId ? { ...n, read: true } : n
      ));
    } catch (error) {
      toast.error(tx('Failed', 'Error', 'Failed'));
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsRead();
      setNotifications(notifications.map(n => ({ ...n, read: true })));
      toast.success(tx('All marked as read', 'Todo marcado como leído', 'All marked as read'));
    } catch (error) {
      toast.error(tx('Failed', 'Error', 'Failed'));
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'package':
        return <Package size={20} className="text-[#0077FF]" />;
      case 'payment':
        return <CreditCard size={20} className="text-[#FFB300]" />;
      case 'success':
        return <Check size={20} className="text-green-500" />;
      default:
        return <Info size={20} className="text-gray-500" />;
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <Layout title={t('notifications')}>
      <div className="space-y-6" data-testid="notifications-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">
            {unreadCount > 0 
              ? `${unreadCount} ${tx('unread', 'sin leer', 'unread')}`
              : (tx('All caught up', 'Todo al día', 'All caught up'))}
          </span>
          {unreadCount > 0 && (
            <Button variant="outline" onClick={handleMarkAllRead} className="rounded-lg">
              <Check size={16} className="mr-2" />
              {t('markAllRead')}
            </Button>
          )}
        </div>

        {/* Notifications List */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="kpi-card">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-8 h-8 border-4 border-[#0077FF] border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : notifications.length > 0 ? (
            <div className="divide-y divide-gray-100">
              {notifications.map((notification, index) => (
                <motion.div
                  key={notification.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className={`p-4 flex items-start gap-4 ${!notification.read ? 'bg-teal-50/50' : ''} hover:bg-gray-50 transition-colors`}
                >
                  <div className={`p-2 rounded-xl ${!notification.read ? 'bg-[#0077FF]/10' : 'bg-gray-100'}`}>
                    {getNotificationIcon(notification.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h4 className={`font-semibold ${!notification.read ? 'text-gray-900' : 'text-gray-700'}`}>
                          {notification.title}
                        </h4>
                        <p className="text-sm text-gray-600 mt-1">{notification.message}</p>
                        <p className="text-xs text-gray-400 mt-2">
                          {new Date(notification.created_at).toLocaleString()}
                        </p>
                      </div>
                      {!notification.read && (
                        <button
                          onClick={() => handleMarkRead(notification.id)}
                          className="p-1.5 hover:bg-white rounded-lg text-[#0077FF]"
                        >
                          <Check size={18} />
                        </button>
                      )}
                    </div>
                  </div>
                  {!notification.read && (
                    <div className="w-2 h-2 bg-[#0077FF] rounded-full mt-2"></div>
                  )}
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <Bell size={48} className="mx-auto mb-3 opacity-50" />
              <p className="font-medium">{t('noNotifications')}</p>
            </div>
          )}
        </motion.div>

        {/* Notification Types */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="kpi-card">
          <h3 className="text-lg font-bold tracking-wide text-gray-900 mb-4" style={{ fontFamily: 'Bebas Neue' }}>
            {t('notificationTypes')}
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#0077FF]/10 rounded-xl">
                <Package size={18} className="text-[#0077FF]" />
              </div>
              <span className="text-sm text-gray-600">{t('packages')}</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#FFB300]/10 rounded-xl">
                <CreditCard size={18} className="text-[#FFB300]" />
              </div>
              <span className="text-sm text-gray-600">{t('payments')}</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-xl">
                <Check size={18} className="text-green-600" />
              </div>
              <span className="text-sm text-gray-600">{tx('Success', 'Éxito', 'Succès')}</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-100 rounded-xl">
                <Info size={18} className="text-gray-600" />
              </div>
              <span className="text-sm text-gray-600">{tx('Info', 'Info', 'Info')}</span>
            </div>
          </div>
        </motion.div>
      </div>
    </Layout>
  );
}
